<template>
  <div class="top-container">
    <div class="patient-summary">
      <div class="descriptor-container">
        <div class="label">{{ tr('MRN') }}</div>
        <div class="datum">{{ patientData.mrn || '—' }}</div>
      </div>
      <div class="age-sex">
        <div class="descriptor-container">
          <div class="label age">{{ tr(ageLabel) }}</div>
          <div class="datum">{{ patientData.age >= 0 ? patientData.age : '—' }}</div>
        </div>
        <div class="descriptor-container">
          <div class="label">{{ tr('Sex') }}</div>
          <div class="datum">{{ patientData.sex || '—' }}</div>
        </div>
      </div>
      <div class="descriptor-container">
        <div class="label">{{ tr('Mortality Status') }}</div>
        <div class="datum">{{ patientData.mortalityStatus || '—' }}</div>
      </div>
      <div class="descriptor-container">
        <div class="label">{{ tr('Primary Diagnosis') }}</div>
        <div class="datum">{{ patientData.diagnosis || '—' }}</div>
      </div>
      <div class="descriptor-container">
        <div class="label">{{ tr('Clinical Stage') }}</div>
        <div class="datum">{{ patientData.staging || '—' }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 dātma, inc™
//
// Unauthorized copying of this file, via any medium is strictly prohibited
// Proprietary and confidential

import { computed, inject, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { getRecord } from './patientEhr.js'
import { compareDatesAscending, compareDatesDescending } from '@/utils/utils'

const tr = inject('tr')
const store = useStore()
const ageLabel = ref('Age')

const patientData = reactive({
  firstName: '',
  lastName: '',
  sex: '',
  mrn: '',
  age: -1,
  diagnosis: '',
  staging: '',
  mortalityStatus: '',
})

const patient = computed(() => store.getters.session.patient)

const loadPatientData = async () => {
  patientData.firstName = patient.value.given || ''
  patientData.lastName = patient.value.family || ''
  patientData.sex = patient.value.gender || ''
  patientData.mrn = (patient.value.id?.length > 0 ? patient.value.id.split(',') : [''])[0]
  patientData.age = patient.value.age || -1
  patientData.mortalityStatus = patient.value.mortalityStatus || patient.value.mortality_status || ''
  ageLabel.value = patientData.mortalityStatus === 'Deceased' ? 'Age at Time of Death' : 'Age'

  // getRecord provides us with diagnosis and clinical staging from a tumorboard session endpoint
  const record = await getRecord(patientData.mrn)

  if (!record?.Upcoming?.length && !record?.Past?.length) {
    patientData.diagnosis = ''
    patientData.staging = ''
  } else {
    // Diagnosis: prefer Upcoming session for diagnosis
    // get earliest upcoming session else most recent past session
    const recordForDiagnosis = (record?.Upcoming.length ?
      record.Upcoming.toSorted(compareDatesAscending) : record.Past.toSorted(compareDatesDescending))[0]
    patientData.diagnosis = recordForDiagnosis.diagnosis || ''

    // Staging: we only see staging in past sessions
    // so use the most recent past record
    if (!record?.Past?.length) {
      patientData.staging = ''
    } else {
      const mostRecentPastRecord = record.Past.toSorted(compareDatesDescending)[0]
      patientData.staging = mostRecentPastRecord.recording_info?.clinical_staging ?
        Object.keys(mostRecentPastRecord.recording_info.clinical_staging)[0] : ''
    }
  }

  // TODO: ultimately, we don't want to store off the diagnosis we get here from TBSession for genomics usage.
  // we want something else tied to the specimen. So this will ultimately go away
  // https://datma.atlassian.net/browse/DATMA360-597
  const cardState = {
    diagnosis: patientData.diagnosis,
  }
  store.dispatch('setGenomicsResults', { genomicsResults: cardState, publish: true })
}

watch(patient, () => {
  loadPatientData()
})

await loadPatientData()
</script>

<style lang="scss" scoped>
.top-container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

.patient-summary {
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  text-align: left;
  padding-bottom: 16px;
}

.descriptor-container {
  margin: 8px 15px 0;
  width: fit-content;
  display: block;
  align-content: left;
}

.datum {
  font-weight: 500;
  font-size: 0.8rem;
}

.age-sex {
  display: flex;
}

.age {
  margin-right: 1rem;
  width: 100%;
}

.descriptor-label {
  color: var(--Grey500);
  display: block;
  align-content: left;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.38px;
}
</style>
